import type { Ref } from 'vue'
import type { OptionsProps, SelectOption, SelectValue } from '../SelectTypes'
import { debounce } from 'es-toolkit'
import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue'
import { normalizeOption, sameOptionValue } from '../SelectTypes'

export type { OptionFunction, OptionsProps, OptionType } from '../SelectTypes'

export function useOptionsResolver(
  props: OptionsProps,
  { search, searchFilter }: { search: Ref<string>; searchFilter: (text: string) => boolean },
) {
  const resolved = shallowRef<SelectOption[]>([])
  const pending = ref(0)
  const loading = computed(() => pending.value > 0)
  const error = shallowRef<unknown>()
  let request = 0
  let disposed = false
  let currentRequest: Promise<SelectOption[]> = Promise.resolve([])

  async function useLoading<T>(resolve: () => T | Promise<T>): Promise<T> {
    pending.value++
    try {
      return await resolve()
    } finally {
      pending.value--
    }
  }

  const eo = computed(() => {
    const source = props.options ?? []
    if (typeof source === 'function') {
      return resolved.value
    }
    if (Array.isArray(source)) {
      return source.map(value => normalizeOption(value, props))
    }
    return Object.entries(source).map(([key, label]) => {
      const number = Number(key)
      return normalizeOption(
        {
          [props.valueProp]: !Number.isNaN(number) && String(number) === key ? number : key,
          [props.trackProp]: label,
          [props.labelProp]: label,
        },
        props,
      )
    })
  })

  function findOption(value: SelectValue, options: readonly SelectOption[] = eo.value) {
    return options.find(option => sameOptionValue(option.value, value))
  }

  async function resolveOptions(version: number) {
    const source = props.options
    if (typeof source !== 'function') {
      return
    }
    error.value = undefined
    const promise = useLoading(async () => {
      const options = await source({ ...props.filter, limit: 50, query: search.value })
      return options.map(value => normalizeOption(value, props))
    })
    currentRequest = promise
    try {
      const options = await promise
      if (!disposed && version === request) {
        resolved.value = options
      }
    } catch (cause) {
      if (!disposed && version === request) {
        error.value = cause
      }
    }
  }

  const scheduleResolve = debounce(resolveOptions, 300)
  watch(
    [
      () => props.options,
      () => props.filter,
      search,
      () => props.valueProp,
      () => props.labelProp,
      () => props.trackProp,
    ],
    () => {
      request++
      scheduleResolve.cancel()
      if (typeof props.options === 'function') {
        scheduleResolve(request)
      }
    },
    { immediate: true, deep: true, flush: 'sync' },
  )

  async function searchOptionById(value: SelectValue): Promise<SelectOption> {
    const existing = findOption(value)
    if (existing) {
      return existing
    }
    // A failed list request must not prevent resolving a selected value.
    const requested = await currentRequest.catch(() => [])
    const inRequest = findOption(value, requested)
    if (inRequest) {
      return inRequest
    }
    const source = props.options
    if (typeof source === 'function') {
      const options = await useLoading(() => source({ ...props.filter, limit: 50, query: value }))
      const found = findOption(
        value,
        options.map(item => normalizeOption(item, props)),
      )
      if (found) {
        return found
      }
    }
    return normalizeOption(value, props)
  }

  const filteredOptions = computed(() =>
    typeof props.options === 'function' || !search.value
      ? eo.value
      : eo.value.filter(option => searchFilter(option.searchText)),
  )

  onScopeDispose(() => {
    disposed = true
    request++
    scheduleResolve.cancel()
  })

  return { eo, filteredOptions, loading, error, useLoading, findOption, searchOptionById }
}
